import { useEffect, useRef, useState } from 'react';
import Spinner from 'react-spinkit';
import {
  getCurrentYearTeachersData,
  getTotalRow,
  getLegacyStudentLogins,
  getNewStudentLogins,
  getLegacyStoryOpens,
  getNewStoryOpens,
} from '../reportUtilities';
import SortableTable from '../Tables/SortableTable';
import { cloneDeep } from 'lodash';
import styles from './TableStyles.module.scss';
import { ReportStudent, ReportingMixpanelEvent, TeacherClassroomMaps } from '@peekapak/types/ReportDataExchangeTypes';
import { UserProfileType } from '@peekapak/types/DataProtocolTypes';
import { Assignment } from '@peekapak/types/LessonPlanTypes';
import TeacherClassroomModal from './TeacherClassroomModal';

interface OverallUsageProps {
  errorMessage: string;
  gameProgressMap: Record<
    string,
    {
      completionPercent: number;
      displayName: string;
      id: string;
      lastDateOpened: number;
      readingLevel: number;
      timeSpent: number;
      type: string;
    }[]
  >;
  gameProgressMapReady: boolean;
  gameProgressMapLoadProgress: string;
  journalsMap: Record<
    string,
    {
      classroomId: string;
      createdAt: number;
      feeling: number;
      type: string;
      userId: string;
    }[]
  >;
  journalsMapReady: boolean;
  journalsMapLoadProgress?: string;
  legacyPeekavilleEvents: ReportingMixpanelEvent[];
  legacyPeekavilleEventsReady: boolean;
  teacherStudentMap: Record<string, ReportStudent[]>;
  teacherStudentMapReady: boolean;
  teacherClassroomMaps: TeacherClassroomMaps;
  teacherClassroomMapsReady: boolean;
  userList: UserProfileType[];
  legacyPlatformEvents: ReportingMixpanelEvent[];
  legacyPlatformEventsReady: boolean;
  newMixpanelEvents: ReportingMixpanelEvent[];
  newMixpanelEventsReady: boolean;
  startDate: Date | null;
  endDate: Date | null;
}

const StudentsElementary = ({
  teacherStudentMapReady,
  teacherStudentMap,
  teacherClassroomMaps,
  teacherClassroomMapsReady,
  userList,
  legacyPeekavilleEvents,
  legacyPeekavilleEventsReady,
  gameProgressMap,
  gameProgressMapReady,
  gameProgressMapLoadProgress,
  journalsMap,
  journalsMapReady,
  journalsMapLoadProgress,
  errorMessage,
  legacyPlatformEvents,
  legacyPlatformEventsReady,
  newMixpanelEvents,
  newMixpanelEventsReady,
  startDate,
  endDate,
}: OverallUsageProps) => {
  const [myState, setMyState] = useState({
    dataMap: {} as Record<
      string,
      {
        firstName: string;
        middleName: string;
        lastName: string;
        userId: string;
        username: string;
        grade: number;
        language: string;
        assignments?: Assignment[];
        events: ReportingMixpanelEvent[];
        gameProgress: {
          completionPercent: number;
          displayName: string;
          id: string;
          lastDateOpened: number;
          readingLevel: number;
          timeSpent: number;
          type: string;
        }[];
        journals: {
          classroomId: string;
          createdAt: number;
          feeling: number;
          type: string;
          userId: string;
        }[];
      }[]
    >,
    dataMapReady: false,
  });

  const [modalState, setModalState] = useState({
    isOpen: false,
    teacherName: '',
    teacherEmail: '',
  });

  // const prevMixpanelEventsReady = useRef(legacyPeekavilleEventsReady);

  // useEffect(() => {
  //   if (prevMixpanelEventsReady.current && !legacyPeekavilleEventsReady) {
  //     setMyState((prevState) => ({
  //       ...prevState,
  //       dataMap: {},
  //       dataMapReady: false,
  //     }));
  //   }
  //   prevMixpanelEventsReady.current = legacyPeekavilleEventsReady;
  // }, [legacyPeekavilleEvents, newMixpanelEvents, journalsMap]);

  useEffect(() => {
    setMyState((prevState) => ({
      ...prevState,
      dataMap: {},
      dataMapReady: false,
    }));
  }, [legacyPeekavilleEvents, newMixpanelEvents, journalsMap]);

  const gameProgressMapRef = useRef(gameProgressMap);

  useEffect(() => {
    const gameProgressChanged = gameProgressMap !== gameProgressMapRef.current;
    if (gameProgressChanged) gameProgressMapRef.current = gameProgressMap;

    if (
      journalsMapReady &&
      gameProgressMapReady &&
      legacyPeekavilleEventsReady &&
      teacherStudentMapReady &&
      teacherClassroomMapsReady &&
      legacyPlatformEventsReady &&
      newMixpanelEventsReady &&
      (!myState.dataMapReady || gameProgressChanged)
    ) {
      buildData();
    }

    function buildData() {
      const newMap = {} as Record<
        string,
        {
          firstName: string;
          middleName: string;
          lastName: string;
          userId: string;
          username: string;
          grade: number;
          language: string;
          assignments?: Assignment[];
          events: ReportingMixpanelEvent[];
          gameProgress: {
            completionPercent: number;
            displayName: string;
            id: string;
            lastDateOpened: number;
            readingLevel: number;
            timeSpent: number;
            type: string;
          }[];
          journals: {
            classroomId: string;
            createdAt: number;
            feeling: number;
            type: string;
            userId: string;
          }[];
        }[]
      >;
      const allSeen = new Set<string>();
      Object.keys(teacherStudentMap).forEach((teacherKey) => {
        const newStudentsArray = teacherStudentMap[teacherKey]
          .filter((s) => s.grade < 6)
          .filter((s) => {
            if (allSeen.has(s.userId)) return false;
            allSeen.add(s.userId);
            return true;
          })
          .map((s) => {
            const events = [...legacyPeekavilleEvents, ...newMixpanelEvents, ...legacyPlatformEvents].filter(
              (event) => (event.userId || event.distinct_id) === s.userId,
            );

            return {
              ...s,
              events,
              gameProgress: gameProgressMap[s.userId],
              journals: journalsMap[s.userId],
            };
          });

        newMap[teacherKey] = newStudentsArray;
      });

      setMyState((prevState) => ({
        ...prevState,
        dataMap: newMap,
        dataMapReady: true,
      }));
    }
  }, [
    teacherClassroomMapsReady,
    legacyPeekavilleEventsReady,
    legacyPeekavilleEvents,
    teacherStudentMap,
    teacherStudentMapReady,
    gameProgressMapReady,
    gameProgressMap,
    journalsMap,
    journalsMapReady,
    myState.dataMapReady,
    legacyPlatformEventsReady,
    userList,
    newMixpanelEventsReady,
  ]);

  const message = getDataNotReadyMessage();
  if (message) {
    return message;
  }

  const staticMessage = getStaticMessage();
  if (staticMessage) {
    return staticMessage;
  }

  const rosteredTeachers = getCurrentYearTeachersData(
    userList,
    teacherClassroomMaps,
    legacyPlatformEvents,
    newMixpanelEvents,
  );

  const sortedList = cloneDeep(rosteredTeachers).sort((a, b) => (a.lastName || '').localeCompare(b.lastName || ''));

  const combinedEvents = [...legacyPeekavilleEvents, ...legacyPlatformEvents, ...newMixpanelEvents];

  const rosteredStudentIds = new Set(
    Object.values(teacherStudentMap)
      .flat()
      .map((s) => s.userId),
  );

  const allStudentIds = [
    ...new Set(
      combinedEvents
        .filter((e) => e.type === 'Student')
        .map((e) => e.userId || e.distinct_id)
        .filter(Boolean),
    ),
  ];
  const otherStudentIds = allStudentIds.filter((id) => !rosteredStudentIds.has(id));

  const otherEvents = combinedEvents.filter(
    (e) => (e.userId || e.distinct_id) && otherStudentIds.includes(e.userId || e.distinct_id),
  );

  const tableData = sortedList.map((e) => {
    return {
      teacher_name: `${e.firstName || ''} ${e.lastName || ''}`,
      rostered_student: getNumberOfRosteredStudents(e.email),
      student_logins: getNumberOfStudentLogins(e.email),
      activities_completed: getNumberOfGamesPlayed(e.email),
      book_opens: getNumberOfBooksRead(e.email),
      feelings_checkins: getNumberOfJournals(e.email),
      email: e.email,
      userId: e.userId,
    };
  });

  if (otherStudentIds.length > 0) {
    const otherGamesCount = otherStudentIds.reduce((acc, id) => {
      const progress = gameProgressMap[id] || [];
      return acc + progress.filter((g) => g.type === 'mini-game' && g.completionPercent > 0).length;
    }, 0);
    const otherJournals = otherStudentIds.reduce((acc, id) => acc + ((journalsMap[id] as any[])?.length || 0), 0);
    tableData.push({
      teacher_name: 'Other (Unassociated)',
      rostered_student: otherStudentIds.length,
      student_logins: getLegacyStudentLogins(otherEvents) + getNewStudentLogins(otherEvents),
      activities_completed: otherGamesCount,
      book_opens: getLegacyStoryOpens(otherEvents) + getNewStoryOpens(otherEvents),
      feelings_checkins: otherJournals,
      email: null,
      userId: null,
    });
  }

  const handleTeacherClick = (data: { teacher_name?: string; email?: string }) => {
    if (!data.email) return;
    setModalState({
      isOpen: true,
      teacherName: data.teacher_name || '',
      teacherEmail: data.email || '',
    });
  };

  const columns = [
    {
      label: "Teacher's Name",
      accessor: 'teacher_name',
      sortable: false,
      clickable: (data: any) => data.email != null,
      onClick: handleTeacherClick,
      renderTooltip: (data: { teacher_name?: string }) =>
        data.teacher_name === 'Other (Unassociated)'
          ? 'Any data that cannot be associated with a user. This is mainly due to the original accounts and/or classrooms having been deleted'
          : `View student breakdown by classroom for ${data.teacher_name || ''}`,
    },
    {
      label: 'Number of Students',
      accessor: 'rostered_student',
      sortable: true,
    },
    {
      label: 'Student Logins',
      accessor: 'student_logins',
      sortable: true,
    },
    {
      label: 'Activities Completed',
      accessor: 'activities_completed',
      sortable: true,
    },
    {
      label: 'Book Opens',
      accessor: 'book_opens',
      sortable: true,
    },
    {
      label: 'Feelings Check-Ins',
      accessor: 'feelings_checkins',
      sortable: true,
    },
  ];

  const totalRow = getTotalRow(tableData);

  const getTeacherData = () => {
    const teacher = teacherClassroomMaps.maps.find((t) => t.email === modalState.teacherEmail);
    return teacher || null;
  };

  // console.debug(
  //   `%cStudent Analytics active students`,
  //   'background: blue; color: yellow',
  //   totalActiveStudents
  // );

  const teacherData = getTeacherData();

  return (
    <div>
      <SortableTable data={tableData} columns={columns} totals={totalRow} />
      {teacherData && (
        <TeacherClassroomModal
          isOpen={modalState.isOpen}
          startDate={startDate}
          endDate={endDate}
          onClose={() => setModalState((prev) => ({ ...prev, isOpen: false }))}
          teacherName={modalState.teacherName}
          teacherEmail={modalState.teacherEmail}
          classrooms={teacherData.classrooms}
          allEvents={[...legacyPeekavilleEvents, ...legacyPlatformEvents, ...newMixpanelEvents]}
          journalsMap={journalsMap}
          gameProgressMap={gameProgressMap}
          minGrade={-1}
          maxGrade={5}
        />
      )}
    </div>
  );

  function getStaticMessage() {
    if (userList.length === 0) {
      return (
        <div className={styles.messageContainer}>
          <div className={styles.progressMessage}>There are no users in this school</div>
        </div>
      );
    }

    if (errorMessage) {
      return (
        <div className={styles.messageContainer}>
          <div className={styles.progressMessage}>
            {`We're sorry, there's a problem retrieving data. Please contact
            Peekapak support and provide them with this error message:`}
            {errorMessage}
          </div>
        </div>
      );
    }
  }

  function getDataNotReadyMessage() {
    let message = null;

    if (!teacherStudentMapReady) {
      message = 'Matching students to teachers...';
    } else if (!legacyPeekavilleEventsReady) {
      message = 'Loading game events data...';
    } else if (!gameProgressMapReady) {
      message = `Loading game progress data ${gameProgressMapLoadProgress}...`;
    } else if (!journalsMapReady) {
      message = `Loading student check-in data ${journalsMapLoadProgress}...`;
    } else if (!myState.dataMapReady) {
      message = 'Calculating stats...';
    }

    if (message) {
      return (
        <div className={styles.messageContainer}>
          <div className={styles.progressMessage}>
            {message}
            <Spinner name='line-scale' />
            This may take up to 10 minutes or more
          </div>
        </div>
      );
    }

    return null;
  }

  function getNumberOfJournals(email: string) {
    const studentArray = myState.dataMap[email];
    if (studentArray && studentArray.length > 0) {
      const numberOfJournalEntries = studentArray.map((s) => {
        if (s.journals) {
          return s.journals.reduce((acc) => acc + 1, 0);
        }

        return 0;
      });

      return numberOfJournalEntries.reduce((acc, e) => acc + e, 0);
    }
    return 0;
  }

  function getNumberOfBooksRead(email: string) {
    const studentArray = myState.dataMap[email];
    if (studentArray && studentArray.length > 0) {
      const eventsArray = studentArray.reduce(
        (acc, s) => [...acc, ...s.events],
        [] as unknown as ReportingMixpanelEvent[],
      );

      return getLegacyStoryOpens(eventsArray) + getNewStoryOpens(eventsArray);
    }
    return 0;
  }

  function getNumberOfGamesPlayed(email: string) {
    const studentArray = myState.dataMap[email];

    if (studentArray && studentArray.length > 0) {
      const gamesPlayed = studentArray.map((s) => {
        if (s.gameProgress && Array.isArray(s.gameProgress)) {
          return s.gameProgress.reduce((acc, g) => {
            if (g?.type === 'mini-game' && g?.completionPercent > 0) return acc + 1;
            return acc;
          }, 0);
        }

        return 0;
      });

      return gamesPlayed.reduce((acc, gp) => acc + gp, 0);
    }
    return 0;
  }

  function getNumberOfStudentLogins(email: string) {
    const studentArray = myState.dataMap[email];
    const eventsArray = studentArray.reduce(
      (acc, s) => [...acc, ...s.events],
      [] as unknown as ReportingMixpanelEvent[],
    );

    return getLegacyStudentLogins(eventsArray) + getNewStudentLogins(eventsArray);
  }

  function getNumberOfRosteredStudents(email: string) {
    const studentArray = myState.dataMap[email];
    if (studentArray && studentArray.length > 0) {
      return studentArray.length;
    }
    return 0;
  }
};

export default StudentsElementary;
